import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Bell,
  BellOff,
  MessageSquare,
  Star,
  CreditCard,
  FileWarning,
  Plus,
  ArrowRight,
  User as UserIcon,
  ChevronRight,
  ChevronDown,
  FilePlus2,
  Users as UsersIcon,
  Megaphone,
  Car,
  Loader2,
  CalendarClock,
  Bot,
  Trash2,
  Phone as PhoneIcon,
  Mail as MailIcon,
  MessageCircle,
  X,
} from "lucide-react";
import { toast } from "sonner";

function greeting() {
  const h = new Date().getHours();
  if (h < 6) return "Buenas noches";
  if (h < 13) return "Buenos días";
  if (h < 20) return "Buenas tardes";
  return "Buenas noches";
}

const HERO_BG =
  "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=2000&q=80";

const TYPE_META = {
  chat_from_driver: { icon: MessageSquare, color: "#0EA5E9", label: "Chat conductor" },
  chat_from_staff: { icon: MessageSquare, color: "#0EA5E9", label: "Chat interno" },
  rating_bad: { icon: Star, color: "#E63946", label: "Mala reseña" },
  rating_good: { icon: Star, color: "#059669", label: "Nueva reseña" },
  payment_received: { icon: CreditCard, color: "#059669", label: "Pago recibido" },
  complaint_new: { icon: FileWarning, color: "#D97706", label: "Reclamación" },
  service_no_show: { icon: CalendarClock, color: "#E63946", label: "No show" },
  service_cancelado: { icon: CalendarClock, color: "#E63946", label: "Cancelado" },
  client_invoice: { icon: FileWarning, color: "#7C3AED", label: "Factura solicitada" },
  client_time_change: { icon: CalendarClock, color: "#7C3AED", label: "Cambio de hora" },
  client_other: { icon: MessageSquare, color: "#7C3AED", label: "Consulta cliente" },
  ai_chat_escalation: { icon: Bot, color: "#F59E0B", label: "Escalado IA" },
  public_booking_new: { icon: Plus, color: "#0EA5E9", label: "Nueva reserva web" },
  no_show_evidence_uploaded: { icon: FileWarning, color: "#E63946", label: "Pruebas subidas" },
};

function relativeTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "hace un momento";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

function NotificationRow({ n, onOpen, onDelete }) {
  const meta = TYPE_META[n.type] || { icon: Bell, color: "#0A0A0A", label: "Aviso" };
  const Icon = meta.icon;
  const isRead = n.__me_read;
  const isEscalation = n.type === "ai_chat_escalation";
  const [expanded, setExpanded] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState(null);

  const toggle = async () => {
    const willOpen = !expanded;
    setExpanded(willOpen);
    if (willOpen && !isRead) onOpen(n, { navigate: false });
    if (willOpen && isEscalation && n.chat_session_id && !chatMessages) {
      setChatLoading(true);
      try {
        const { data } = await api.get(`/ai-chat/sessions/${n.chat_session_id}`);
        setChatMessages(data.messages || []);
      } catch (e) {
        console.error(e);
        setChatMessages([]);
      } finally {
        setChatLoading(false);
      }
    }
  };

  const clientPhone = n.client_phone || "";
  const clientEmail = n.client_email || "";
  const cleanPhone = clientPhone.replace(/[^\d+]/g, "").replace(/^\+/, "");

  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta notificación?")) return;
    try {
      await api.delete(`/notifications/${n.id}`);
      onDelete(n.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className={`border-b border-zinc-100 ${isRead ? "opacity-70" : "bg-white"}`}
      data-testid={`notification-${n.id}`}
    >
      <button
        onClick={toggle}
        className="w-full text-left flex items-start gap-3 p-4 hover:bg-zinc-50 transition-colors"
      >
        <div
          className="w-10 h-10 shrink-0 flex items-center justify-center border-2"
          style={{ borderColor: meta.color, color: meta.color }}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-base">{n.title}</div>
            {!isRead && (
              <span className="w-2.5 h-2.5 rounded-full bg-[#E63946] shrink-0" aria-label="No leído" />
            )}
          </div>
          {n.message && (
            <div className="text-sm text-zinc-700 mt-1 leading-relaxed whitespace-pre-wrap break-words">
              {n.message}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2 flex items-center gap-2">
            <span>{meta.label} · {relativeTime(n.created_at)}</span>
            {n.client_booking_number && (
              <span className="text-zinc-700 font-mono">· {n.client_booking_number}</span>
            )}
          </div>
        </div>
        {expanded ? (
          <ChevronDown size={14} className="text-zinc-400 mt-2 shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-zinc-400 mt-2 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-zinc-100 bg-zinc-50">
          {/* Full details */}
          {(n.client_name || clientPhone || clientEmail || n.client_booking_number) && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {n.client_name && (
                <div><b>Cliente:</b> {n.client_name}</div>
              )}
              {n.client_booking_number && (
                <div><b>Reserva:</b> {n.client_booking_number}</div>
              )}
              {clientPhone && (
                <div><b>Teléfono:</b> {clientPhone}</div>
              )}
              {clientEmail && (
                <div className="break-all"><b>Email:</b> {clientEmail}</div>
              )}
              {isEscalation && (
                <div>
                  <b>Verificado:</b> {n.client_verified ? "Sí" : "No"}
                </div>
              )}
            </div>
          )}

          {/* AI conversation for escalations */}
          {isEscalation && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                <Bot size={12} /> Conversación con la IA
              </div>
              {chatLoading ? (
                <div className="text-sm text-zinc-500 flex items-center gap-2 py-2">
                  <Loader2 className="animate-spin" size={13} /> Cargando…
                </div>
              ) : chatMessages && chatMessages.length > 0 ? (
                <div className="border-2 border-black bg-white max-h-72 overflow-y-auto p-2 space-y-2">
                  {chatMessages.map((m, i) => (
                    <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
                      <div
                        className={`max-w-[85%] px-3 py-2 text-[13px] whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-black text-white"
                            : m.escalated
                            ? "bg-amber-50 border border-amber-400"
                            : "bg-zinc-100"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-zinc-500 italic">Sin conversación disponible.</div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            {clientPhone && (
              <>
                <a
                  href={`https://wa.me/${cleanPhone}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-black bg-white hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
                  data-testid={`notif-${n.id}-whatsapp`}
                >
                  <MessageCircle size={12} /> WhatsApp
                </a>
                <a
                  href={`tel:${clientPhone}`}
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white"
                  data-testid={`notif-${n.id}-call`}
                >
                  <PhoneIcon size={12} /> Llamar
                </a>
                <a
                  href={`sms:${clientPhone}`}
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white"
                >
                  SMS
                </a>
              </>
            )}
            {clientEmail && (
              <a
                href={`mailto:${clientEmail}`}
                className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-black bg-white hover:bg-black hover:text-white"
                data-testid={`notif-${n.id}-email`}
              >
                <MailIcon size={12} /> Email
              </a>
            )}
            {n.link && (
              <button
                onClick={() => onOpen(n, { navigate: true })}
                className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-black bg-black text-white hover:bg-[#F5B301] hover:text-black hover:border-[#F5B301]"
                data-testid={`notif-${n.id}-open`}
              >
                Abrir <ArrowRight size={11} />
              </button>
            )}
            <button
              onClick={del}
              className="ml-auto inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-widest border border-[#E63946] text-[#E63946] bg-white hover:bg-[#E63946] hover:text-white"
              data-testid={`notif-${n.id}-delete`}
            >
              <Trash2 size={12} /> Borrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loadingN, setLoadingN] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);

  const isDriver = user?.role === "driver";
  const isStaff = user?.role === "admin" || user?.role === "user";

  const load = async () => {
    try {
      const [{ data }, statsRes] = await Promise.all([
        api.get("/notifications", { params: { limit: 30 } }),
        api.get("/services/stats/summary").catch(() => ({ data: null })),
      ]);
      const withRead = data.map((n) => ({ ...n, __me_read: (n.read_by || []).includes(user.id) }));
      setNotifications(withRead);
      if (statsRes?.data) setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingN(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.__me_read).length,
    [notifications]
  );

  const openNotification = async (n, opts = { navigate: true }) => {
    if (!n.__me_read) {
      try {
        await api.post("/notifications/mark-read", { ids: [n.id] });
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, __me_read: true } : x)));
      } catch {}
    }
    if (opts.navigate && n.link) navigate(n.link);
  };

  const deleteNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const markAllRead = async () => {
    setRefreshing(true);
    try {
      await api.post("/notifications/mark-read", {});
      setNotifications((prev) => prev.map((x) => ({ ...x, __me_read: true })));
      toast.success("Notificaciones marcadas como leídas");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const quickActions = isDriver
    ? [
        { label: "Mis servicios", icon: Car, to: "/driver", testid: "quick-driver" },
        { label: "Chat con supervisor", icon: MessageSquare, to: "/chat", testid: "quick-chat" },
        { label: "Comunicados", icon: Megaphone, to: "/announcements", testid: "quick-announcements" },
      ]
    : [
        { label: "Nueva reserva", icon: FilePlus2, to: "/services/new", testid: "quick-new-service" },
        { label: "Reservas", icon: Car, to: "/services", testid: "quick-services" },
        { label: "Reclamaciones", icon: FileWarning, to: "/complaints", testid: "quick-complaints" },
        { label: "Chat conductores", icon: MessageSquare, to: "/chat", testid: "quick-chat-staff" },
        { label: "Conductores", icon: UsersIcon, to: "/users", testid: "quick-users" },
        { label: "Comunicados", icon: Megaphone, to: "/announcements", testid: "quick-announcements-staff" },
      ];

  const photoUrl = user?.photo_path
    ? `${API}/users/${user.id}/photo?auth=${encodeURIComponent(getToken())}`
    : null;

  return (
    <div className="min-h-[calc(100vh-2rem)] bg-zinc-50" data-testid="home-page">
      {/* Hero */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-50 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto h-full flex flex-col justify-end px-4 sm:px-8 pb-14 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 border-2 border-[#F5B301] rounded-full overflow-hidden bg-black/40 flex items-center justify-center shadow-2xl">
              {photoUrl ? (
                <img src={photoUrl} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon size={24} />
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5B301]">
                {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })}
              </div>
              <div className="text-xs text-white/60 uppercase tracking-widest mt-1">Centro de Control</div>
            </div>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[0.95]">
            {greeting()},<br />
            <span className="text-[#F5B301]">{user?.name?.split(" ")[0] || "equipo"}</span>
          </h1>
          <p className="text-sm text-white/70 mt-3 max-w-lg">
            {isDriver ? "Aquí verás tus servicios y comunicados." : "Aquí está el pulso de la empresa hoy."}
          </p>
        </div>
      </section>

      {/* Stats bar */}
      {isStaff && stats && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 -mt-8 relative z-20 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Hoy"
              value={stats.today || 0}
              hint="servicios programados"
              accent="#F5B301"
              icon={CalendarClock}
              testid="stat-today"
            />
            <StatTile
              label="En curso"
              value={(stats.by_status?.en_camino || 0) + (stats.by_status?.a_bordo || 0)}
              hint="en_camino + a_bordo"
              accent="#0EA5E9"
              icon={Car}
              testid="stat-in-progress"
            />
            <StatTile
              label="Pendiente asig."
              value={stats.by_status?.pendiente_asignacion || 0}
              hint="por asignar conductor"
              accent="#E63946"
              icon={UsersIcon}
              testid="stat-unassigned"
            />
            <StatTile
              label="Total"
              value={stats.total || 0}
              hint="reservas en el sistema"
              accent="#059669"
              icon={FilePlus2}
              testid="stat-total"
            />
          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick actions */}
        <section className="lg:col-span-2">
          <h2 className="font-display text-sm uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
            <span className="w-8 h-px bg-black" /> Acceso rápido
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {quickActions.map((a, idx) => {
              const Icon = a.icon;
              const accents = ["#F5B301", "#0EA5E9", "#E63946", "#059669", "#7C3AED", "#D97706"];
              const accent = accents[idx % accents.length];
              return (
                <Link
                  key={a.to}
                  to={a.to}
                  className="group relative border border-zinc-200 bg-white p-5 hover:border-black hover:shadow-xl transition-all flex flex-col justify-between min-h-[140px] overflow-hidden"
                  data-testid={a.testid}
                >
                  <div
                    className="absolute top-0 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: accent }}
                  />
                  <div
                    className="w-11 h-11 flex items-center justify-center"
                    style={{ backgroundColor: `${accent}15`, color: accent }}
                  >
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold mt-4">{a.label}</div>
                    <ArrowRight
                      size={14}
                      className="mt-2 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                    />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Featured card */}
          {isStaff && (
            <Link
              to="/services/new"
              className="mt-6 block border-2 border-black bg-gradient-to-br from-black via-zinc-900 to-black text-white p-7 hover:shadow-2xl transition-shadow group relative overflow-hidden"
              data-testid="home-cta-new-service"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#F5B301]/20 rounded-full blur-3xl group-hover:bg-[#F5B301]/40 transition-colors" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5B301] mb-2">Recomendado</div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-display text-2xl sm:text-3xl font-bold">
                      Crear una nueva reserva
                    </div>
                    <div className="text-sm opacity-70 mt-2 max-w-md">
                      Calcula precio, asigna conductor y envía la confirmación al cliente en 1 minuto.
                    </div>
                  </div>
                  <div className="w-14 h-14 border-2 border-[#F5B301] flex items-center justify-center group-hover:bg-[#F5B301] group-hover:text-black transition-colors shrink-0">
                    <Plus size={26} />
                  </div>
                </div>
              </div>
            </Link>
          )}
        </section>

        {/* Notifications feed */}
        <aside>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-sm uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-2">
              <Bell size={14} /> Notificaciones
              {unreadCount > 0 && (
                <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                  {unreadCount}
                </span>
              )}
            </h2>
            {notifications.length > 0 && (
              <button
                onClick={markAllRead}
                disabled={refreshing || unreadCount === 0}
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-black disabled:opacity-40"
                data-testid="mark-all-read-btn"
              >
                {refreshing ? <Loader2 className="animate-spin" size={12} /> : "Marcar todo leído"}
              </button>
            )}
          </div>
          <div className="border border-zinc-200 bg-white overflow-hidden shadow-sm">
            {loadingN ? (
              <div className="p-6 text-center text-zinc-500 text-sm">Cargando…</div>
            ) : notifications.length === 0 ? (
              <div className="p-10 text-center">
                <BellOff size={32} className="mx-auto text-zinc-300 mb-3" />
                <div className="text-sm text-zinc-500">Todo tranquilo. Sin novedades.</div>
              </div>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <NotificationRow key={n.id} n={n} onOpen={openNotification} onDelete={deleteNotification} />
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function StatTile({ label, value, hint, accent, icon: Icon, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 p-4 shadow-md hover:shadow-lg transition-shadow relative overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
          <div className="font-display text-3xl font-bold mt-1" style={{ color: accent }}>
            {value}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>
        </div>
        <div
          className="w-9 h-9 flex items-center justify-center rounded"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}
